import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2, CheckCircle, XCircle, Clock, Building2, Building, Calendar, 
  FileText, Coins, Gift, Plus, Trash2, Eye, Search
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  investor: { icon: Building2, label: "Investor", color: "text-teal-600 bg-teal-100" },
  pharma: { icon: Building, label: "Pharma", color: "text-indigo-600 bg-indigo-100" },
  event: { icon: Calendar, label: "Event", color: "text-blue-600 bg-blue-100" },
  grant: { icon: FileText, label: "Grant", color: "text-purple-600 bg-purple-100" }
};

const STATUS_CONFIG = {
  pending: { icon: Clock, label: "Pending", color: "bg-amber-100 text-amber-700" },
  validated: { icon: CheckCircle, label: "Validated", color: "bg-green-100 text-green-700" },
  rejected: { icon: XCircle, label: "Rejected", color: "bg-red-100 text-red-700" }
};

function ContributionCard({ contribution, onView, onValidate }) {
  const category = CATEGORY_CONFIG[contribution.category] || CATEGORY_CONFIG.investor;
  const status = STATUS_CONFIG[contribution.status] || STATUS_CONFIG.pending;
  const Icon = category.icon;
  const StatusIcon = status.icon;

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`contribution-${contribution.contribution_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${category.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <div className="font-medium text-slate-900">
                {contribution.data?.firm_name || contribution.data?.company_name || 
                 contribution.data?.event_name || contribution.data?.grant_name || "Unknown"}
              </div>
              <div className="text-sm text-slate-500">
                {contribution.contribution_type.replace("_", " ")} • by {contribution.user_email}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {new Date(contribution.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={status.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
            <Badge variant="outline">
              <Coins className="w-3 h-3 mr-1" />
              {contribution.pending_credits} credits
            </Badge>
          </div>
        </div>
        
        {contribution.status === "pending" && (
          <div className="flex gap-2 mt-4 pt-3 border-t">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => onView(contribution)}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-1" />
              View Details
            </Button>
            <Button 
              size="sm"
              onClick={() => onValidate(contribution, "approve")}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => onValidate(contribution, "reject")}
              className="flex-1"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContributionDetailDialog({ contribution, open, onClose, onValidate }) {
  const [notes, setNotes] = useState("");
  const [validating, setValidating] = useState(false);

  if (!contribution) return null;

  const handleValidate = async (action) => {
    setValidating(true);
    await onValidate(contribution, action, notes);
    setValidating(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Contribution</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Meta info */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div>
              <span className="text-sm text-slate-500">Submitted by </span>
              <span className="font-medium">{contribution.user_email}</span>
            </div>
            <Badge variant="outline">
              <Coins className="w-3 h-3 mr-1" />
              {contribution.pending_credits} credits
            </Badge>
          </div>
          
          {/* Contribution data */}
          <div className="space-y-3">
            <Label>Contribution Data</Label>
            <div className="bg-slate-100 rounded-lg p-4 font-mono text-sm overflow-auto max-h-64">
              <pre>{JSON.stringify(contribution.data, null, 2)}</pre>
            </div>
          </div>
          
          {/* Quality indicators */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="font-bold text-lg">{Math.round(contribution.quality_score * 100)}%</div>
              <div className="text-xs text-slate-500">Quality Score</div>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="font-bold text-lg">{contribution.data?.confidence_level || "N/A"}</div>
              <div className="text-xs text-slate-500">Confidence</div>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="font-bold text-lg">{contribution.data?.source_type || "N/A"}</div>
              <div className="text-xs text-slate-500">Source Type</div>
            </div>
          </div>
          
          {/* Admin notes */}
          <div className="space-y-2">
            <Label>Admin Notes (optional)</Label>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes for rejection reason or validation comments..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            variant="destructive"
            onClick={() => handleValidate("reject")}
            disabled={validating}
          >
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
            Reject
          </Button>
          <Button 
            onClick={() => handleValidate("approve")}
            disabled={validating}
            className="bg-green-600 hover:bg-green-700"
          >
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Approve ({contribution.pending_credits} credits)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromoCodesSection() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [freeMonths, setFreeMonths] = useState(12);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCodes(data.promo_codes || []);
      }
    } catch (error) {
      console.error("Failed to fetch promo codes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: newCode,
          free_months: freeMonths,
          notes: notes
        })
      });

      if (response.ok) {
        toast.success("Promo code created");
        setShowCreate(false);
        setNewCode("");
        setNotes("");
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create promo code");
      }
    } catch (error) {
      toast.error("Failed to create promo code");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (code) => {
    if (!confirm(`Delete promo code ${code}?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${code}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Promo code deleted");
        fetchCodes();
      } else {
        toast.error("Failed to delete promo code");
      }
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">SBC Promo Codes</h3>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Create Code
        </Button>
      </div>

      {codes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No promo codes created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {codes.map((code) => (
            <Card key={code.code}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <div className="font-mono font-bold">{code.code}</div>
                  <div className="text-sm text-slate-500">
                    {code.free_months} months free
                    {code.used_by && <span className="text-amber-600"> • Used by {code.used_by}</span>}
                    {code.notes && <span> • {code.notes}</span>}
                  </div>
                </div>
                {!code.used_by && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleDelete(code.code)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input 
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="e.g., SBC2026"
              />
            </div>
            <div className="space-y-2">
              <Label>Free Months</Label>
              <Select value={String(freeMonths)} onValueChange={(v) => setFreeMonths(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., SBC Winter 2026 cohort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminContributionsContent() {
  const [loading, setLoading] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchContributions();
    fetchStats();
  }, [statusFilter, categoryFilter]);

  const fetchContributions = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/contributions?status=${statusFilter}`;
      if (categoryFilter !== "all") {
        url += `&category=${categoryFilter}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setContributions(data.contributions || []);
      }
    } catch (error) {
      console.error("Failed to fetch contributions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/contributions/stats`, {
        credentials: "include"
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleValidate = async (contribution, action, notes = "") => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/contributions/${contribution.contribution_id}/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ action, notes })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        if (result.discount_unlocked) {
          toast.success("User unlocked contributor discount!");
        }
        fetchContributions();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to validate");
      }
    } catch (error) {
      toast.error("Failed to validate contribution");
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Contribution Management</h1>
        <p className="text-slate-600 mt-2">Review and validate user-submitted intelligence</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">{stats.pending}</div>
              <div className="text-sm text-slate-500">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.validated}</div>
              <div className="text-sm text-slate-500">Validated</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-sm text-slate-500">Rejected</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-500">Total</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="contributions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="contributions">Contributions</TabsTrigger>
          <TabsTrigger value="promo-codes">Promo Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="contributions" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="validated">Validated</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="investor">Investor</SelectItem>
                <SelectItem value="pharma">Pharma</SelectItem>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="grant">Grant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contributions List */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : contributions.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">No contributions found</h3>
                <p className="text-slate-500">
                  {statusFilter === "pending" 
                    ? "All caught up! No pending contributions to review."
                    : `No ${statusFilter} contributions found.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {contributions.map((contribution) => (
                <ContributionCard
                  key={contribution.contribution_id}
                  contribution={contribution}
                  onView={(c) => { setSelectedContribution(c); setShowDetail(true); }}
                  onValidate={handleValidate}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="promo-codes">
          <PromoCodesSection />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <ContributionDetailDialog
        contribution={selectedContribution}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        onValidate={handleValidate}
      />
    </div>
  );
}

export default function AdminContributions() {
  return (
    <AppLayout>
      {(user) => {
        if (!user?.is_admin) {
          return (
            <div className="flex items-center justify-center min-h-[50vh]">
              <Card className="max-w-md">
                <CardContent className="p-8 text-center">
                  <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                  <p className="text-slate-500">You don't have permission to access this page.</p>
                </CardContent>
              </Card>
            </div>
          );
        }
        return <AdminContributionsContent />;
      }}
    </AppLayout>
  );
}
